import React, { FC, createContext, useContext, ReactNode, useMemo } from 'react';
import { useCompanyLicenses, useSelector } from '~Hooks';
import {
  BAEMINGO_PRODUCT,
  PRODUCT_TYPE,
  SUBSCRIPTION_PAYMENT_STATUS,
} from '../../../__queries__/globalTypes';
import { useQuery } from '@apollo/client';
import { ACTIVE_LICENSES_QUERY } from './api/queries';
import { GetActiveLicenses } from './api/__queries__';
import { useDeviceHandler } from '~Components/DeviceProvider';

/** CompanyIds that pilot Loyalty Pro (in this array since they're not on new licenses) */
const LOYALTY_PRO_PILOT: string[] = ['cl10pouc9pwah0817u9f9dcci']; // 1889

/**
 * This interface tracks two categories of licenses:
 * 1. Venue-level licenses: Features available throughout the entire venue
 * 2. Device-level licenses: Features available on specific devices
 *
 * Used to determine which features should be accessible in the application based on
 * what the venue has licensed/purchased.
 *
 * isXClaimable represents if a device-level license is active on the venue but without a deviceId - meaning it can be claimed by a device
 */
interface LicensesContextType {
  /* -- Venue-level licenses -- */

  /** Mobile-app for the restaurant */
  hasApp: boolean;

  /** Giftcards */
  hasGiftcard: boolean;

  /** Inventory for tracking how much items is left */
  hasInventory: boolean;

  /** Allows linking members in QSR & TSR to give out membership discount & stamps on purchases */
  hasLoyaltyPro: boolean;

  /* -- Device-level licenses -- */

  //** A display for customers to view their orders progress */
  hasCustomerDisplay: boolean;
  atleastOneDeviceHasCustomerDisplay: boolean; // We need to use the syncronizer OrderStatus on all devices if this is true
  isCustomerDisplayClaimable: boolean;

  /** Express POS / Self Service Checkout */
  hasEPOS: boolean;
  isEPOSClaimable: boolean;

  /** Evented Kitchen Display */
  hasKDS: boolean;
  atleastOneDeviceHasKDS: boolean; // We need to use the syncronizer OrderStatus on all devices if this is true
  isKDSClaimable: boolean;

  /** QSR/POS - Multi-QSR is included in this license */
  hasQSR: boolean;
  isQSRClaimable: boolean;

  /** Tables */
  hasTSR: boolean;
  atleastOneDeviceHasTSR: boolean; // Used for instance for Parked orders so other devices with no TSR can move orders to tables
  isTSRClaimable: boolean;
  hasBookings: boolean; // Whether the venue has access to the Bookings feature, used for conditional rendering of Bookings related features across the app

  hasStaffLedger: boolean;

  /** Allows the user to add items to tables, but nothing else. Mostly used for smartphone "telecommanders" */
  hasHandheldOrder: boolean;
  isHandheldOrderClaimable: boolean;

  /** Only allows paying a table and splitting cheques */
  hasHandheldPay: boolean;
  isHandheldPayClaimable: boolean;

  /** Complete 1:1 feature parity with the POS */
  hasHandheldPro: boolean;
  isHandheldProClaimable: boolean;

  /** If true: Superadmin have manually frozen this venue's invoice account - will block pages that requires licenses */
  isPOSAccessBlocked: boolean;

  /** Superadmin can set this to true if there are unpaid invoices, used to show a warning before POS access is blocked */
  isInvoiceWarningActive: boolean;

  /** Function to get the payment status of a Stripe license (always return paid if stripe not connected or if the license is not a Stripe license) */
  getStripeLicensePaymentStatus: (product: BAEMINGO_PRODUCT) => SUBSCRIPTION_PAYMENT_STATUS;

  hasQueryFailed: boolean; // Whether the licenses query has failed
  loading: boolean;
  refetch: () => void;
}

const LicensesContext = createContext<LicensesContextType | undefined>(undefined);

export const useLicenses = () => {
  const context = useContext(LicensesContext);
  if (!context) {
    throw new Error('useLicenses must be used within a LicensesProvider');
  }
  return context;
};

interface Props {
  children: ReactNode;
}

/**
 * Handles licenses for the app. We have three solutions to licenses currently, the (1) is the latest & highest priority
 * (1) ActiveLicenses tables from Venue or ClientDevice
 * (2) CompanyFlags from the company
 * (3) Company.licenses
 */
export const LicensesProvider: FC<Props> = ({ children }) => {
  const deviceHandler = useDeviceHandler();
  const companyLicenses = useCompanyLicenses();

  const companyId = useSelector((state) => state.company);
  const companyFlags = useSelector((state) => state.companyFlags);

  /** Fetches licenses on venue-level or licenses not assigned to any device */
  const venueLicensesQuery = useQuery<GetActiveLicenses>(ACTIVE_LICENSES_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  const licensesOnVenue =
    venueLicensesQuery.data?.operativeViewer?.activeLicenses.map((license) => license.product) ||
    [];
  const activeDeviceLicenses =
    deviceHandler.activeDevice?.activeLicenses.map((license) => license.product) || [];

  const isPOSAccessBlocked = venueLicensesQuery.data?.operativeViewer?.isPOSAccessBlocked;
  const isInvoiceWarningActive = venueLicensesQuery.data?.operativeViewer?.isInvoiceWarningActive;

  const stripePaymentStatus = venueLicensesQuery.data?.operativeViewer?.stripePaymentStatus;

  const getStripeLicensePaymentStatus = (p: BAEMINGO_PRODUCT): SUBSCRIPTION_PAYMENT_STATUS => {
    /** Find the license, whether its a venue or device license on the selected device */
    const foundVenueLicense = venueLicensesQuery.data?.operativeViewer?.activeLicenses.find(
      (license) => license.product === p,
    );

    const foundDeviceLicense = deviceHandler.activeDevice?.activeLicenses.find(
      (license) => license.product === p,
    );

    const foundLicense = foundVenueLicense || foundDeviceLicense;

    /** Not a license bought through Stripe & company is not connected to stripe */
    if (!stripePaymentStatus || foundLicense?.provider !== 'STRIPE') {
      return SUBSCRIPTION_PAYMENT_STATUS.PAID;
    }

    if (stripePaymentStatus === SUBSCRIPTION_PAYMENT_STATUS.OVERDUE) {
      return SUBSCRIPTION_PAYMENT_STATUS.OVERDUE;
    }

    if (stripePaymentStatus === SUBSCRIPTION_PAYMENT_STATUS.UNPAID) {
      return SUBSCRIPTION_PAYMENT_STATUS.UNPAID;
    }

    return SUBSCRIPTION_PAYMENT_STATUS.PAID;
  };

  const atleastOneDeviceHasLicense = (product: BAEMINGO_PRODUCT): boolean => {
    const hasVenueDeviceWithActiveLicense = deviceHandler.allDevices.some((device) =>
      device.activeLicenses.some((license) => license.product === product),
    );

    /**
     * For TSR - We need to mark this as true if one of these handheld licenses is active.
     * This is to make sure the tables functionality works fine if the customer only uses handheld devices with TSR.
     */
    if (product === BAEMINGO_PRODUCT.TSR) {
      return (
        hasVenueDeviceWithActiveLicense ||
        activeDeviceLicenses.includes(BAEMINGO_PRODUCT.HANDHELD_ORDER) ||
        activeDeviceLicenses.includes(BAEMINGO_PRODUCT.HANDHELD_PRO)
      );
    }

    return hasVenueDeviceWithActiveLicense;
  };

  const contextValue = useMemo(
    () => ({
      /* Venue-level licenses */
      hasApp: companyFlags.useNewLicenses
        ? licensesOnVenue.includes(BAEMINGO_PRODUCT.APP)
        : companyFlags.hasAppOrders,
      hasGiftcard: companyFlags.useNewLicenses
        ? licensesOnVenue.includes(BAEMINGO_PRODUCT.GIFTCARD)
        : companyLicenses.hasActiveLicense(PRODUCT_TYPE.GIFTCARD),
      hasInventory: companyFlags.useNewLicenses
        ? licensesOnVenue.includes(BAEMINGO_PRODUCT.INVENTORY)
        : companyFlags.hasInventory,
      hasLoyaltyPro: companyFlags.useNewLicenses
        ? licensesOnVenue.includes(BAEMINGO_PRODUCT.LOYALTY_PRO) ||
          LOYALTY_PRO_PILOT.includes(companyId)
        : LOYALTY_PRO_PILOT.includes(companyId),

      /* Device-level licenses */
      hasCustomerDisplay: companyFlags.useNewLicenses
        ? activeDeviceLicenses.includes(BAEMINGO_PRODUCT.CUSTOMER_DISPLAY)
        : companyFlags.hasCDS,
      atleastOneDeviceHasCustomerDisplay: companyFlags.useNewLicenses
        ? atleastOneDeviceHasLicense(BAEMINGO_PRODUCT.CUSTOMER_DISPLAY)
        : companyFlags.hasCDS,
      isCustomerDisplayClaimable: companyFlags.useNewLicenses
        ? licensesOnVenue.includes(BAEMINGO_PRODUCT.CUSTOMER_DISPLAY)
        : false,

      hasEPOS: companyFlags.useNewLicenses
        ? activeDeviceLicenses.includes(BAEMINGO_PRODUCT.EPOS)
        : companyFlags.hasExpress,
      isEPOSClaimable: companyFlags.useNewLicenses
        ? licensesOnVenue.includes(BAEMINGO_PRODUCT.EPOS)
        : false,

      hasKDS: companyFlags.useNewLicenses
        ? activeDeviceLicenses.includes(BAEMINGO_PRODUCT.KDS)
        : companyFlags.hasKDS,
      atleastOneDeviceHasKDS: companyFlags.useNewLicenses
        ? atleastOneDeviceHasLicense(BAEMINGO_PRODUCT.KDS)
        : companyFlags.hasKDS,
      isKDSClaimable: companyFlags.useNewLicenses
        ? licensesOnVenue.includes(BAEMINGO_PRODUCT.KDS)
        : false,

      hasQSR: companyFlags.useNewLicenses
        ? activeDeviceLicenses.includes(BAEMINGO_PRODUCT.QSR)
        : companyFlags.hasPOS,
      isQSRClaimable: companyFlags.useNewLicenses
        ? licensesOnVenue.includes(BAEMINGO_PRODUCT.QSR)
        : false,

      hasTSR: companyFlags.useNewLicenses
        ? activeDeviceLicenses.includes(BAEMINGO_PRODUCT.TSR)
        : companyFlags.hasTables,
      atleastOneDeviceHasTSR: companyFlags.useNewLicenses
        ? atleastOneDeviceHasLicense(BAEMINGO_PRODUCT.TSR)
        : companyFlags.hasTables,
      isTSRClaimable: companyFlags.useNewLicenses
        ? licensesOnVenue.includes(BAEMINGO_PRODUCT.TSR)
        : false,
      hasBookings: licensesOnVenue.includes(BAEMINGO_PRODUCT.BOOKINGS) || false,

      hasHandheldOrder: companyFlags.useNewLicenses
        ? activeDeviceLicenses.includes(BAEMINGO_PRODUCT.HANDHELD_ORDER)
        : companyLicenses.hasActiveLicense(PRODUCT_TYPE.HANDHELD_ORDER),
      isHandheldOrderClaimable: companyFlags.useNewLicenses
        ? licensesOnVenue.includes(BAEMINGO_PRODUCT.HANDHELD_ORDER)
        : false,

      hasHandheldPay: companyFlags.useNewLicenses
        ? activeDeviceLicenses.includes(BAEMINGO_PRODUCT.HANDHELD_PAY)
        : companyLicenses.hasActiveLicense(PRODUCT_TYPE.HANDHELD_PAY),
      isHandheldPayClaimable: companyFlags.useNewLicenses
        ? licensesOnVenue.includes(BAEMINGO_PRODUCT.HANDHELD_PAY)
        : false,

      hasHandheldPro: companyFlags.useNewLicenses
        ? activeDeviceLicenses.includes(BAEMINGO_PRODUCT.HANDHELD_PRO)
        : companyLicenses.hasActiveLicense(PRODUCT_TYPE.HANDHELD_PRO),
      isHandheldProClaimable: companyFlags.useNewLicenses
        ? licensesOnVenue.includes(BAEMINGO_PRODUCT.HANDHELD_PRO)
        : false,

      isPOSAccessBlocked: companyFlags.useNewLicenses ? Boolean(isPOSAccessBlocked) : false,
      isInvoiceWarningActive: companyFlags.useNewLicenses
        ? Boolean(isInvoiceWarningActive)
        : false,
      getStripeLicensePaymentStatus,

      hasStaffLedger:
        companyFlags.useNewLicenses && licensesOnVenue.includes(BAEMINGO_PRODUCT.STAFF_LEDGER),

      hasQueryFailed:
        companyFlags.useNewLicenses && !venueLicensesQuery.data && !venueLicensesQuery.loading,
      loading: companyLicenses.loading || venueLicensesQuery.loading,
      refetch: () => {
        venueLicensesQuery.refetch();
        deviceHandler.refetch();
      },
    }),
    [
      companyFlags,
      companyLicenses,
      licensesOnVenue,
      activeDeviceLicenses,
      isPOSAccessBlocked,
      deviceHandler.allDevices,
      venueLicensesQuery.loading,
      companyLicenses.loading,
    ],
  );

  return <LicensesContext.Provider value={contextValue}>{children}</LicensesContext.Provider>;
};

export default LicensesProvider;
